import { useMutation } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { changeEmail, changePassword } from '@/features/auth/api'
import { errorText } from '@/features/auth/ResetScreen'

/**
 * Настройки учётной записи: почта и пароль.
 *
 * Обе правки — под текущий пароль: до открытой чужой вкладки дорваться
 * проще, чем угадать пароль, и без этой проверки увести учётную запись
 * можно было бы за два нажатия.
 *
 * Ник не меняется: по нему человека находят в друзьях и подписывают за
 * столом, и смена ника на ходу означала бы, что вчерашний соперник сегодня
 * кто-то другой.
 */
export function SettingsCard() {
  const [open, setOpen] = useState(false)

  return (
    <div className="card">
      <h2 className="card__title">Учётная запись</h2>

      {open ? (
        <>
          <EmailForm />
          <PasswordForm />
          <button className="ghost" type="button" onClick={() => setOpen(false)}>
            свернуть
          </button>
        </>
      ) : (
        <button type="button" onClick={() => setOpen(true)}>
          Изменить почту или пароль
        </button>
      )}
    </div>
  )
}

function EmailForm() {
  const [email, setEmail] = useState('')
  const [current, setCurrent] = useState('')

  const save = useMutation({
    mutationFn: () => changeEmail({ current, email: email.trim() }),
    onSuccess: () => {
      setEmail('')
      setCurrent('')
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate()
  }

  return (
    <form className="form" onSubmit={submit}>
      <h3 className="add-bot__title">Новая почта</h3>
      <input
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="Почта"
        type="email"
        autoComplete="email"
        maxLength={255}
      />
      <input
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        placeholder="Текущий пароль"
        type="password"
        autoComplete="current-password"
        maxLength={72}
      />
      <button type="submit" disabled={save.isPending}>
        Изменить почту
      </button>

      {save.isSuccess && <p className="banner banner--turn">{save.data}</p>}
      {save.isError && <p className="banner banner--error">{errorText(save.error)}</p>}
    </form>
  )
}

function PasswordForm() {
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')

  const save = useMutation({
    mutationFn: () => changePassword({ current, password, password_confirmation: repeat }),
    onSuccess: () => {
      setCurrent('')
      setPassword('')
      setRepeat('')
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate()
  }

  return (
    <form className="form" onSubmit={submit}>
      <h3 className="add-bot__title">Новый пароль</h3>
      <input
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        placeholder="Текущий пароль"
        type="password"
        autoComplete="current-password"
        maxLength={72}
      />
      <input
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        placeholder="Новый пароль"
        type="password"
        autoComplete="new-password"
        maxLength={72}
      />
      <input
        value={repeat}
        onChange={(event) => setRepeat(event.target.value)}
        placeholder="Пароль ещё раз"
        type="password"
        autoComplete="new-password"
        maxLength={72}
      />
      <button type="submit" disabled={save.isPending}>
        Изменить пароль
      </button>

      {save.isSuccess && <p className="banner banner--turn">{save.data}</p>}
      {save.isError && <p className="banner banner--error">{errorText(save.error)}</p>}
    </form>
  )
}
